import {Component, OnDestroy, OnInit} from "@angular/core";
import {Subject, takeUntil} from "rxjs";
import {NotificationService} from "../shared/services/notification.service";
import {MatDialog} from "@angular/material/dialog";
import {ChangeEmailFormComponent} from "./change-email-form/change-email-form.component";
import {ChangePasswordFormComponent} from "./change-password-form/change-password-form.component";
import {EmailPasswordLinkFormComponent} from "./email-password-link-form/email-password-link-form.component";
import {ExternalProvider, FIREBASE_AUTH_PROVIDERS, FirebaseAuthProviderId} from "./auth-providers";
import {MSG_VERIFY_NEW_EMAIL, SignInMethodsFacade} from "./sign-in-methods.facade";
import {SelectContactEmailDialogComponent} from "./select-contact-email-dialog/select-contact-email-dialog.component";
import {AccountService} from "../shared/services/account.service";
import {AuthService} from "../shared/services/auth.service";
import {filter, firstValueFrom} from "rxjs";
import {AppMode, AuthenticatedUser} from "../shared/dm";
import {SettingsService} from "../shared/services/settings.service";
import {ConfirmationDialogComponent} from "../shared/components/confirmation-dialog/confirmation-dialog.component";
import {LoggerService} from "../shared/services/logger.service";
import {LinkedProvider} from "../shared/domain";

@Component({
  selector: "app-sign-in-methods",
  templateUrl: "./sign-in-methods.component.html",
  styleUrls: ["./sign-in-methods.component.scss"],
})
export class SignInMethodsComponent implements OnInit, OnDestroy {
  constructor(
    private readonly dialog: MatDialog,
    private readonly facade: SignInMethodsFacade,
    private readonly notificationService: NotificationService,
    private readonly accountService: AccountService,
    private readonly authService: AuthService,
    private readonly settingsService: SettingsService,
    private readonly logger: LoggerService,
  ) { }

  private readonly destroy$ = new Subject<void>();

  isLoading = true;
  busyFor: string | null = null; // providerId currently processing
  busyChangeEmail = false;
  busyChangePassword = false;

  currentMode: AppMode = AppMode.WEB;
  linkedProvidersData: LinkedProvider[] = [];

  readonly emailVerificationMessage = MSG_VERIFY_NEW_EMAIL;

  get showProgress(): boolean {
    return this.isLoading || this.busyChangeEmail || this.busyChangePassword || this.busyFor != null;
  }
  get isWebMode(): boolean {
    return this.currentMode === AppMode.WEB;
  }

  /**
   * Returns identifying information for a given provider.
   * Returns the email associated with the provider from backend data.
   */
  getProviderIdentifier(providerId: string): string | null {
    return this.getProviderEmail(providerId);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async ngOnInit(): Promise<void> {
    this.settingsService.mode$.subscribe(mode => {
      this.currentMode = mode;
    });

    this.authService.reauthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshLinkedProviders());

    // Load linked providers from backend API (no Firebase auth needed)
    try {
      this.isLoading = true;
      this.linkedProvidersData = await firstValueFrom(this.accountService.getLinkedProviders());
    } catch (e) {
      this.logger.error('Failed to load linked providers', e);
      this.notificationService.showErrorMessage($localize`:@@common.msg.unexpectedError:Unexpected error. Please try again.`);
    } finally {
      this.isLoading = false;
    }
  }

  get linkedProviders(): ExternalProvider[] {
    const ids = new Set(this.linkedProvidersData.map(p => p.providerId));
    return FIREBASE_AUTH_PROVIDERS.filter(p => ids.has(p.id));
  }

  get availableProviders(): ExternalProvider[] {
    const linkedIds = new Set(this.linkedProviders.map(p => p.id))
    return FIREBASE_AUTH_PROVIDERS.filter(p => !linkedIds.has(p.id))
  }

  get linkedCount(): number {
    return this.linkedProviders.length
  }

  getProviderEmail(providerId: string): string | null {
    const provider = this.linkedProvidersData.find(p => p.providerId === providerId);
    return provider?.email || null;
  }

  async refreshLinkedProviders(): Promise<void> {
    try {
      this.linkedProvidersData = await firstValueFrom(this.accountService.getLinkedProviders());
    } catch (e) {
      this.logger.error('Failed to refresh linked providers', e);
    }
  }

  upsertLinkedProvider(provider: LinkedProvider) {
    this.linkedProvidersData = [
      ...this.linkedProvidersData.filter(p => p.providerId !== provider.providerId),
      provider,
    ];
  }

  async openChangeEmailDialog(): Promise<void> {
    // Authenticate FIRST, before showing the email dialog
    this.busyChangeEmail = true;
    try {
      const authenticated = await this.facade.ensureFreshSignIn()
      if (!authenticated) {
        return; // User cancelled authentication
      }

      const dialogRef = this.dialog.open(ChangeEmailFormComponent, {
        width: '320px',
        disableClose: true,
        data: { initialEmail: this.getProviderIdentifier(FirebaseAuthProviderId.PASSWORD) }
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      if (!result) return; // canceled

      const success = await this.facade.changeEmail(result.email);
      if (success) {
        // Loading values from API is blocked, add password login locally
        this.upsertLinkedProvider({
          providerId: FirebaseAuthProviderId.PASSWORD,
          email: result.email,
        })
      }
    } finally {
      this.busyChangeEmail = false;
    }
  }


  async openChangePasswordDialog(): Promise<void> {
    this.busyChangePassword = true;
    try {
      const authenticated = await this.facade.ensureFreshSignIn()
      if (!authenticated) {
        return; // User cancelled authentication
      }

      const dialogRef = this.dialog.open(ChangePasswordFormComponent, {
        width: '320px',
        disableClose: true,
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      if (!result) return; // User cancelled

      await this.facade.changePassword(result.password);
    } finally {
      this.busyChangePassword = false;
    }
  }

  async openAddPasswordSignInDialog(): Promise<void> {
    // Authenticate FIRST, before showing the email/password dialog
    this.busyFor = FirebaseAuthProviderId.PASSWORD;
    try {
      const authenticated = await this.facade.ensureFreshSignIn();
      if (!authenticated) {
        return; // User cancelled authentication
      }

      // Now show the dialog to get email and password
      const dialogRef = this.dialog.open(EmailPasswordLinkFormComponent, {
        width: '320px',
        disableClose: true,
        data: { initialEmail: this.getProviderIdentifier(FirebaseAuthProviderId.PASSWORD) }
      });

      const result = await firstValueFrom(dialogRef.afterClosed())
      if (!result) return; // canceled

      const success = await this.facade.linkEmailPassword(result.email, result.password);
      if (success) {
        // Loading values from API is blocked, add password login locally
        this.upsertLinkedProvider({
          providerId: FirebaseAuthProviderId.PASSWORD,
          email: result.email,
        })
      }
    } finally {
      this.busyFor = null;
    }
  }

  async addSignInMethod(providerId: string): Promise<void> {
    if (providerId === FirebaseAuthProviderId.PASSWORD) {
      this.openAddPasswordSignInDialog()
      return
    }
    this.busyFor = providerId

    try {
      await this.facade.linkSsoProvider(providerId);
      await this.refreshLinkedProviders();
    } finally {
      this.busyFor = null
    }
  }

  async removeSignInMethod(providerId: string): Promise<void> {
    if (this.linkedCount <= 1) {
      this.notificationService.showInfoMessage?.($localize`:@@section.signInMethods.msg.atLeastOneSignInMethodNeeded:At least one log-in method must be kept linked.`);
      return;
    }

    // Authenticate FIRST, before showing the confirmation dialog
    this.busyFor = providerId;
    try {
      const authenticated = await this.facade.ensureFreshSignIn();
      if (!authenticated) {
        return; // User cancelled authentication
      }
    } finally {
      this.busyFor = null;
    }

    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        message: $localize`:@@section.signInMethods.confirmRemove.message:Do you really want to remove this log-in method? You will no longer be able to use it to access your account.`,
        confirmButtonText: $localize`:@@common.action.remove:Remove`,
        confirmButtonColor: 'warn' as const
      }
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      // Canceled by user
      return;
    }

    try {
      const authUser = await firstValueFrom(
        this.authService.user$.pipe(
          filter((u): u is AuthenticatedUser => u !== undefined)
        )
      );

      const verifiedEmails = await firstValueFrom(this.accountService.loadVerifiedEmails());
      if (verifiedEmails.includes(authUser.email)) {
        this.busyFor = providerId;
        try {
          await this.facade.unlinkProvider(providerId);
          await this.refreshLinkedProviders();
        } finally {
          this.busyFor = null;
        }
        return;
      }

      if (verifiedEmails.length === 0) {
        this.notificationService.showInfoMessage?.($localize`:@@section.signInMethods.msg.noAlternativeContactEmail:Before removing this log-in method, you need to set another email address on your account as a contact email.`);
        return;
      }

      // Require the user to select a new contact email from the verified list
      const dialogRef = this.dialog.open(SelectContactEmailDialogComponent, {
        width: '320px',
        disableClose: true,
        data: {
          availableEmails: verifiedEmails,
          currentEmail: authUser.email
        }
      });

      const result: { email: string } | undefined = await firstValueFrom(dialogRef.afterClosed());
      if (!result || !result.email) {
        return; // canceled
      }

      this.busyFor = providerId;
      try {
        // Skip request if email stays same
        if (result.email != authUser.email) {
          await firstValueFrom(this.accountService.updateContactEmail(result.email));
          // Update local auth user state
          this.authService.updateUser({
            ...authUser,
            email: result.email
          });
        }

        // Now unlink the provider
        await this.facade.unlinkProvider(providerId);
        await this.refreshLinkedProviders();
      } finally {
        this.busyFor = null;
      }
    } catch (_) {
      // If anything fails (e.g., loading user or verified emails), fall back to original behavior
      this.busyFor = providerId;
      try {
        await this.facade.unlinkProvider(providerId);
        await this.refreshLinkedProviders();
      } finally {
        this.busyFor = null;
      }
    }
  }
}
